import type { ReactNode } from "react";
import { allDocPages } from "./content";
import DocsLayout from "./layout";

const markdown = `
This guide covers calling CloudQuote from a **web browser, mobile app, or desktop app** — anywhere code is delivered to and executed by the user.

The big constraint: anything in client code is public. CloudQuote is built around that assumption.

## What "front-end" means here

Front-end applications include:

- Websites that fetch data via \`fetch\` / AJAX.
- Single-page apps in React, Vue, Angular, Svelte, etc.
- Desktop apps (Electron, native, Metro).
- Mobile apps (iOS, Android, React Native, Flutter).

What they have in common: the binary or page source is delivered to the user and **must be assumed insecure**.

## Two supported authentication paths

| Path | When to use it |
| :---- | :---- |
| **Whitelisted domain + CORS** | Most websites and SPAs. Simplest setup. |
| **Public app key + user key (or OAuth)** | When you need per-user authorization or your application doesn't have a stable origin domain. |

## Path 1: Whitelisted domain (most common)

This is the default for browser apps. You whitelist your domain in the API Dashboard, and from then on browser requests from that origin are accepted with **no key**.

### Set it up

1. Sign in to the API Dashboard.
2. Open **My Account → API Credentials**.
3. Add a whitelisted domain — e.g., \`example.com\`.

Subdomains are accepted automatically: a \`example.com\` whitelist also covers \`app.example.com\`, \`staging.example.com\`, etc.

### Make a request

\`\`\`js
const response = await fetch(
  "https://api.cloudquote.io/fcon/getQuote.json?symbol=AAPL"
);
const quote = await response.json();
\`\`\`

No \`Authorization\` header. The browser adds the \`Origin\` and \`Referer\` headers automatically; CloudQuote checks those against your whitelist.

### Rules CloudQuote enforces

- Request must include valid CORS headers.
- \`Referer:\` and \`Origin:\` must both match the whitelisted domain (or a subdomain).
- The request must **not** include a private application key. (If it does, the request is rejected — this is what protects you from accidentally leaking a server key in client code.)
- Traffic patterns must not match known abuse heuristics. Aggressive spam triggers automatic throttling.

## Path 2: Public key + user key

Use this when your application has end users and you want each user authorized individually — for example, a trading app where each user has their own subscription tier.

### Flow

1. Your app authenticates the user (your existing login, OAuth, or whatever).
2. After login, your back end issues a **short-lived user key** (an OAuth access token, JWT, or any verifiable credential).
3. Your front end calls CloudQuote with both the **public app key** and the **user key**.

### Make a request

\`\`\`js
const response = await fetch(
  "https://api.cloudquote.io/fcon/getQuote.json?symbol=AAPL",
  {
    headers: {
      "Authorization": "publicKey YOUR_PUBLIC_KEY userKey USER_OAUTH_TOKEN"
    }
  }
);
\`\`\`

CloudQuote calls back to a verification endpoint **you define** to confirm the user key, and re-verifies on a configurable interval.

### Why short-lived

User keys must expire quickly — **1 to 5 minutes is the recommended ceiling**. Longer-lived keys are easy to spoof if a user's session is compromised. Refresh from your back end on every render.

## Common pitfalls

- **Pasting a private key into front-end code.** Don't. Private keys are rejected if the request comes from a browser, but more importantly, the key itself is now public and must be rotated.
- **Origin mismatch behind a CDN or reverse proxy.** Some proxies strip the \`Origin\` header. Make sure your edge config preserves it.
- **\`localhost\` in development.** Add \`localhost\` (and any port you use) as a whitelisted domain for development; remove it before going to production.
- **CORS preflight failures.** If you're using a custom request header (e.g., \`X-Trace-Id\`), the browser will issue an \`OPTIONS\` preflight. CloudQuote handles the preflight automatically as long as the origin is whitelisted.

## A complete React example

\`\`\`jsx
import { useEffect, useState } from "react";

export function QuoteCard({ symbol }) {
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    fetch(\`https://api.cloudquote.io/fcon/getQuote.json?symbol=\${symbol}\`)
      .then(r => r.json())
      .then(setQuote);
  }, [symbol]);

  if (!quote) return <div>Loading…</div>;
  return (
    <div>
      <strong>{quote.symbol}</strong>: \${quote.price.toFixed(2)} ({quote.percentChange.toFixed(2)}%)
    </div>
  );
}
\`\`\`

That's the entire integration when your domain is whitelisted.

## Where to go next

- For server-side integration patterns, see [Back-End Integration](/docs/backend-integration).
- For full credential reference, see [Authentication & API Keys](/docs/authentication).
`;

export default function FrontendIntegrationPage(): ReactNode {
  const page = allDocPages.find(p => p.slug === "frontend-integration")!;
  return <DocsLayout page={page} markdown={markdown}/>;
}
